import { useEffect, useState } from 'react';
import { listMyStorefrontAppointments } from '../../../../api/storefront/client';

const parseAppointmentDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value?.toDate === 'function') {
    try {
      const date = value.toDate();
      return Number.isNaN(date.getTime()) ? null : date;
    } catch {
      return null;
    }
  }
  if (value?.seconds) return new Date(value.seconds * 1000);
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const mapStorefrontClientAppointment = (appointment) => {
  const dateTime = parseAppointmentDate(appointment?.dateTime);
  return {
    ...appointment,
    dateTime,
    endTime: parseAppointmentDate(appointment?.endTime),
    startTime: dateTime,
    isArchived: Boolean(appointment?.archived),
    storageLocation: 'company',
    ...(appointment?.archived
      ? {
          serviceName: appointment.serviceName || 'Appointment',
          status: appointment.status || 'archived',
        }
      : {}),
  };
};

/**
 * Loads active + archived company appointments for the signed-in storefront client.
 */
export const useStorefrontClientAppointments = (clientUser) => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!clientUser?.companyId || (!clientUser?.email && !clientUser?.id)) {
      setAppointments([]);
      setLoading(false);
      setError(null);
      return undefined;
    }

    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        setError(null);
        const rows = await listMyStorefrontAppointments({
          companyId: clientUser.companyId,
          archived: 'all',
        });
        const combined = rows
          .map(mapStorefrontClientAppointment)
          .filter((apt) => apt.startTime instanceof Date && !Number.isNaN(apt.startTime.getTime()))
          .sort((a, b) => b.startTime - a.startTime);

        if (!cancelled) {
          setAppointments(combined);
        }
      } catch (err) {
        console.error('Storefront appointment history fetch failed:', err);
        if (!cancelled) {
          setError(`Failed to load appointments (${err?.status || err?.code || 'unknown_error'})`);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [clientUser?.companyId, clientUser?.id, clientUser?.email]);

  return { appointments, loading, error };
};
